import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, Music2, Rocket, Wrench, X } from 'lucide-react';

import Header from './Header';
import Footer from './Footer';
import ParticleBackground from './ParticleBackground';
import WaveAnimation from './WaveAnimation';
import EffectsPanel from '@/pages/Settings/EffectsPanel';
import ThemePanel from '@/pages/Settings/ThemePanel';
import FloatingToolbar from './FloatingToolbar';
import Live2DWidget from '@/components/common/Live2DWidget';
import FloatingMusicPlayer from '@/components/common/FloatingMusicPlayer';
import { useThemeStore } from '@/store/theme.store.ts';
import { useEffectsStore } from '@/store/effects.store.ts';
import { fetchPublicSiteConfig } from '@/api/site-config.api';

const MainLayout: React.FC = () => {
  const { initializeTheme } = useThemeStore();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [effectsOpen, setEffectsOpen] = useState(false);
  const [floatingMenuOpen, setFloatingMenuOpen] = useState(false);
  const [customEffectScript, setCustomEffectScript] = useState('');
  const [floatingVisibility, setFloatingVisibility] = useState({
    musicPlayer: true,
    toolbar: true,
    scrollTop: true,
  });
  const { applySiteEffectsConfig } = useEffectsStore();

  // 初始化主题
  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  // 监听滚动，显示回到顶部按钮
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const onOpenTheme = () => setThemeOpen(true);
    const onOpenFloatingMenu = () => setFloatingMenuOpen(true);
    const onOpenMusicPlayer = () => {
      setFloatingVisibility((prev) => ({ ...prev, musicPlayer: true }));
      window.dispatchEvent(new Event('music-player:open'));
    };
    const onHideToolbar = () => {
      setFloatingVisibility((prev) => ({ ...prev, toolbar: false }));
    };

    window.addEventListener('theme-panel:open', onOpenTheme);
    window.addEventListener('floating-settings:open', onOpenFloatingMenu);
    window.addEventListener('footer:open-music-player', onOpenMusicPlayer);
    window.addEventListener('floating-toolbar:hide', onHideToolbar);

    return () => {
      window.removeEventListener('theme-panel:open', onOpenTheme);
      window.removeEventListener('floating-settings:open', onOpenFloatingMenu);
      window.removeEventListener('footer:open-music-player', onOpenMusicPlayer);
      window.removeEventListener('floating-toolbar:hide', onHideToolbar);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadSiteEffects = async () => {
      const config = await fetchPublicSiteConfig();
      if (cancelled) return;
      applySiteEffectsConfig(config.effects);
      setFloatingVisibility({
        musicPlayer: config.music?.enabled === true,
        toolbar: true,
        scrollTop: true,
      });
      const script = config.effects?.enabled !== false
        ? (Array.isArray(config.effects?.items)
            ? config.effects.items
                .filter((item: any) => item?.enabled && String(item?.script || '').trim())
                .map((item: any) => String(item?.script || '').trim())
                .join('\n')
            : String(config.effects?.customScript || '').trim())
        : '';
      setCustomEffectScript(script);
    };
    loadSiteEffects();
    return () => {
      cancelled = true;
    };
  }, [applySiteEffectsConfig]);

  useEffect(() => {
    const scriptId = 'site-effects-custom-script';
    const existing = document.getElementById(scriptId);
    if (existing) {
      existing.remove();
    }
    if (!customEffectScript) return;

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'text/javascript';
    script.text = customEffectScript;
    document.body.appendChild(script);

    return () => {
      const mounted = document.getElementById(scriptId);
      if (mounted) mounted.remove();
    };
  }, [customEffectScript]);

  const handleCloseTheme = () => {
    setThemeOpen(false);
  };

  const handleCloseEffects = () => {
    setEffectsOpen(false);
  };

  const floatingMenuPanel = (
    <AnimatePresence>
      {floatingMenuOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[56] bg-black/45"
            onClick={() => setFloatingMenuOpen(false)}
          />
          <motion.aside
            initial={{ opacity: 0, x: 320 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 320 }}
            transition={{ type: 'spring', damping: 24, stiffness: 220 }}
            className="fixed right-0 top-0 h-full w-[320px] max-w-[92vw] bg-[var(--bg-card)] border-l border-[var(--border-light)] shadow-2xl z-[62]"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-light)]">
              <h2 className="text-base font-semibold text-[var(--text-primary)]">悬浮按钮临时设置</h2>
              <button
                type="button"
                onClick={() => setFloatingMenuOpen(false)}
                className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                当前仅对本次浏览会话生效。关闭浏览器或下次进入时，将恢复后台配置状态。
              </p>

              <button
                type="button"
                onClick={() => setFloatingVisibility((prev) => ({ ...prev, musicPlayer: !prev.musicPlayer }))}
                className="w-full px-3 py-3 border border-[var(--border-light)] text-left flex items-center justify-between hover:bg-black/[0.03] dark:hover:bg-white/[0.03]"
              >
                <span className="inline-flex items-center gap-2 text-sm text-[var(--text-primary)]">
                  <Music2 size={15} /> 音乐播放器
                </span>
                <span className="text-xs text-[var(--text-secondary)]">{floatingVisibility.musicPlayer ? '显示' : '隐藏'}</span>
              </button>

              <button
                type="button"
                onClick={() => setFloatingVisibility((prev) => ({ ...prev, toolbar: !prev.toolbar }))}
                className="w-full px-3 py-3 border border-[var(--border-light)] text-left flex items-center justify-between hover:bg-black/[0.03] dark:hover:bg-white/[0.03]"
              >
                <span className="inline-flex items-center gap-2 text-sm text-[var(--text-primary)]">
                  <Wrench size={15} /> 悬浮菜单
                </span>
                <span className="text-xs text-[var(--text-secondary)]">{floatingVisibility.toolbar ? '显示' : '隐藏'}</span>
              </button>

              <button
                type="button"
                onClick={() => setFloatingVisibility((prev) => ({ ...prev, scrollTop: !prev.scrollTop }))}
                className="w-full px-3 py-3 border border-[var(--border-light)] text-left flex items-center justify-between hover:bg-black/[0.03] dark:hover:bg-white/[0.03]"
              >
                <span className="inline-flex items-center gap-2 text-sm text-[var(--text-primary)]">
                  <ArrowUp size={15} /> 回到顶部
                </span>
                <span className="text-xs text-[var(--text-secondary)]">{floatingVisibility.scrollTop ? '显示' : '隐藏'}</span>
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );

  // 为每个页面创建独立的特效组件实例，使用 location.pathname 作为 key
  const effectsKey = `effects-${location.pathname}`;

  // 首页使用全屏布局
  if (isHomePage) {
    return (
      <>
        {/* 全局背景特效 - 使用独立 key 确保重新初始化 */}
        <ParticleBackground key={`particle-${effectsKey}`} />
        <WaveAnimation key={`wave-${effectsKey}`} />
        
        <div className="min-h-screen relative z-10">
          <Header />
          <main className="w-full">
            <Outlet />
          </main>
          <Footer />
        </div>
        
        {/* 主题设置面板 */}
        <ThemePanel isOpen={themeOpen} onClose={handleCloseTheme} />

        {/* 特效设置面板 */}
        <EffectsPanel isOpen={effectsOpen} onClose={handleCloseEffects} />
        
        {/* 悬浮工具栏 */}
        {floatingVisibility.toolbar ? (
          <FloatingToolbar onOpenTheme={() => setThemeOpen(true)} onOpenEffects={() => setEffectsOpen(true)} />
        ) : null}

        {/* Live2D AI 宠物 */}
        <Live2DWidget />

        {/* 悬浮音乐播放器 */}
        {floatingVisibility.musicPlayer ? <FloatingMusicPlayer /> : null}
        
        {/* 快速回到顶部按钮 */}
        <AnimatePresence>
          {floatingVisibility.scrollTop && showScrollTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="fixed bottom-24 md:bottom-8 right-4 md:right-6 z-[67] w-12 h-12 rounded-2xl border shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(145deg, var(--bg-card, #ffffff), color-mix(in srgb, var(--accent-color, #2563eb) 14%, var(--bg-card, #ffffff)))',
                color: 'var(--accent-color, #2563eb)',
                borderColor: 'color-mix(in srgb, var(--accent-color, #2563eb) 40%, var(--border-light, #d1d5db))',
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              title="回到顶部"
            >
              <Rocket size={19} strokeWidth={2.2} />
            </motion.button>
          )}
        </AnimatePresence>

        {floatingMenuPanel}
      </>
    );
  }

  // 其他页面使用容器布局（也显示特效）
  return (
    <>
      {/* 全局背景特效 - 使用独立 key 确保重新初始化 */}
      <ParticleBackground key={`particle-${effectsKey}`} />
      <WaveAnimation key={`wave-${effectsKey}`} />
      
      <div className="min-h-screen flex flex-col relative z-10">
        {/* 头部导航 */}
        <Header />
        
        {/* 主内容区域 */}
        <main className="flex-1">
          <div className="container mx-auto px-4 py-6 max-w-7xl">
            <Outlet />
          </div>
        </main>
        
        {/* 底部 */}
        <Footer />
      </div>
      
      {/* 主题设置面板 */}
      <ThemePanel isOpen={themeOpen} onClose={handleCloseTheme} />

      {/* 特效设置面板 */}
      <EffectsPanel isOpen={effectsOpen} onClose={handleCloseEffects} />
      
      {/* 悬浮工具栏 */}
      {floatingVisibility.toolbar ? (
        <FloatingToolbar onOpenTheme={() => setThemeOpen(true)} onOpenEffects={() => setEffectsOpen(true)} />
      ) : null}

      {/* Live2D AI 宠物 */}
      <Live2DWidget />

      {/* 悬浮音乐播放器 */}
      {floatingVisibility.musicPlayer ? <FloatingMusicPlayer /> : null}
      
      {/* 快速回到顶部按钮 */}
      <AnimatePresence>
        {floatingVisibility.scrollTop && showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-24 md:bottom-8 right-4 md:right-6 z-[67] w-12 h-12 rounded-2xl border shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(145deg, var(--bg-card, #ffffff), color-mix(in srgb, var(--accent-color, #2563eb) 14%, var(--bg-card, #ffffff)))',
              color: 'var(--accent-color, #2563eb)',
              borderColor: 'color-mix(in srgb, var(--accent-color, #2563eb) 40%, var(--border-light, #d1d5db))',
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            title="回到顶部"
          >
            <Rocket size={19} strokeWidth={2.2} />
          </motion.button>
        )}
      </AnimatePresence>

      {floatingMenuPanel}
    </>
  );
};

export default MainLayout;
